import { useId } from 'react';
import tw from 'twin.macro';
import { useAnimatedGaugeValue } from './hooks/useAnimatedGaugeValue';

const TRACK_COLOR = '#E4E7EB';
const KNOB_COLOR = '#475016';
// Sampled from the design's linear gradient — light yellow-green at the arc's
// start, deepening to dark green at the tip.
const GRADIENT_STOPS = [
  { offset: '0%', color: '#CBD59C' },
  { offset: '25%', color: '#94A738' },
  { offset: '50%', color: '#8DA12B' },
  { offset: '75%', color: '#475016' },
  { offset: '100%', color: '#0E0E0E' },
];

// viewBox geometry (scales as a whole via the SVG's responsive width). CX/CY are
// derived from the box + radius so the arc always sits centered and unclipped,
// however VIEW_W/VIEW_H/CENTER_R are tuned.
const CENTER_R = 144;
const STROKE_WIDTH = 26;
const KNOB_R = 15;
const VIEW_W = 330;
const VIEW_H = 190;
const CX = VIEW_W / 2;
const CY = (VIEW_H + CENTER_R) / 2;

// The overlaid number/%/caption were tuned against this default width. The SVG
// arc always scales with `width` via its viewBox, but plain text doesn't scale
// on its own — without this, a narrower gauge (e.g. portrait mobile passing a
// smaller `width`) shrinks the arc while the text stays full-size, pushing the
// caption past the bottom of the arc's bowl.
const BASE_WIDTH = 370;
const NUMBER_FONT_SIZE = 60;
const PERCENT_FONT_SIZE = 34;
const CAPTION_FONT_SIZE = 18;
const NUMBER_GAP = 8;
const CAPTION_MARGIN_TOP = 4;
// Where the readout block's centre sits, as a share of the SVG's height. Pushed
// down from the old 75% — the design leaves a clear gap between the arc's inner
// edge and the top of the number, and the number itself is a step smaller so the
// caption still lands inside the bowl rather than under the arc's ends.
// Expressed as a percentage (not a fixed offset) so it holds at any `width`,
// since the SVG scales with its viewBox.
const VALUE_ANCHOR_PERCENT = 79;

function pointOnArc(angleDeg, radiusX, radiusY) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + radiusX * Math.cos(rad), y: CY - radiusY * Math.sin(rad) };
}

function describeArc(startAngle, endAngle, radiusX, radiusY) {
  const start = pointOnArc(startAngle, radiusX, radiusY);
  const end = pointOnArc(endAngle, radiusX, radiusY);
  const largeArc = Math.abs(startAngle - endAngle) > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${radiusX} ${radiusY} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

/**
 * Half-circle completion gauge: a single-color progress arc (with a knob dot at
 * its tip) over a light gray track, with the percentage + caption below it.
 */
const CompletionGauge = ({
  percentage = 0,
  width = 370,
  strokeWidth = STROKE_WIDTH,
  knobRadius = KNOB_R,
  radiusX = CENTER_R,
  radiusY = CENTER_R,
  numberStyle,
  percentSignStyle,
  captionStyle,
  overlapStyle,
  caption,
  onAnimationComplete,
  // Vertical clearance between the arc and the readout, as a share of the gauge's
  // height. Raise it to push the number further down into the bowl.
  valueAnchorPercent = VALUE_ANCHOR_PERCENT,
}) => {
  const gradientId = useId();
  const textScale = width / BASE_WIDTH;
  const target = Math.max(0, Math.min(100, Number(percentage) || 0));
  const animatedPercentage = useAnimatedGaugeValue(target, onAnimationComplete);
  const displayValue = Math.round(animatedPercentage);
  const endAngle = 180 - (animatedPercentage / 100) * 180;
  const startPoint = pointOnArc(180, radiusX, radiusY);
  const knobPoint = pointOnArc(endAngle, radiusX, radiusY);

  return (
    <div css={tw`relative flex flex-col items-center`} style={{ width }}>
      <svg
        width="100%"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="img"
        aria-label={caption}
        css={tw`block shrink-0`}
      >
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={startPoint.x}
            y1={startPoint.y}
            x2={knobPoint.x}
            y2={knobPoint.y}
          >
            {GRADIENT_STOPS.map(stop => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
            ))}
          </linearGradient>
        </defs>
        <path
          d={describeArc(180, 0, radiusX, radiusY)}
          fill="none"
          stroke={TRACK_COLOR}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {animatedPercentage > 0 && (
          <>
            <path
              d={describeArc(180, endAngle, radiusX, radiusY)}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
            <circle cx={knobPoint.x} cy={knobPoint.y} r={knobRadius} fill={KNOB_COLOR} />
          </>
        )}
      </svg>
      {/* Text is absolutely centered inside the arc's bowl. Anchoring to a
          percentage of the SVG height (instead of a fixed negative margin)
          keeps it aligned at any `width`, since the SVG scales responsively. */}
      <div
        css={[
          tw`pointer-events-none absolute inset-x-0 flex flex-col items-center -translate-y-1/2`,
          overlapStyle,
        ]}
        style={{ top: `${valueAnchorPercent}%` }}
      >
        <div dir="ltr" css={[tw`flex items-baseline`, { gap: `${NUMBER_GAP * textScale}px` }]}>
          <span
            css={[
              tw`font-bold leading-none text-Primary_800`,
              { fontSize: `${NUMBER_FONT_SIZE * textScale}px` },
              numberStyle,
            ]}
          >
            {displayValue}
          </span>
          <span
            css={[
              tw`font-bold leading-none text-Primary_800`,
              { fontSize: `${PERCENT_FONT_SIZE * textScale}px` },
              percentSignStyle,
            ]}
          >
            %
          </span>
        </div>
        <span
          css={[
            tw`font-bold text-Primary_800`,
            {
              fontSize: `${CAPTION_FONT_SIZE * textScale}px`,
              marginTop: `${CAPTION_MARGIN_TOP * textScale}px`,
            },
            captionStyle,
          ]}
        >
          {caption}
        </span>
      </div>
    </div>
  );
};

export default CompletionGauge;
